import { Howl, Howler } from "howler";

type AudioCueKey =
  | "button_tap"
  | "scan_success"
  | "scan_fail"
  | "quest_complete"
  | "token_award"
  | "item_granted"
  | "badge_connect"
  | "notification"
  | "achievement"
  | "error";

type AudioCueDefinition = {
  freq: number;
  duration: number;
  type: "sine" | "triangle" | "sawtooth" | "square";
  volume: number;
};

export type WMUAudioAPI = {
  play: (name: string) => boolean;
  has: (name: string) => boolean;
};

type WMUAudioWindow = Window & {
  WMUAudio?: WMUAudioAPI;
  __WMU_AUDIO_BOOTED?: boolean;
  __WMU_AUDIO_EVENT_HANDLER__?: EventListener;
};

const SAMPLE_RATE = 22050;
const ATTACK_SECONDS = 0.008;
const RELEASE_SECONDS = 0.03;

const cues: Record<AudioCueKey, AudioCueDefinition> = {
  button_tap: { freq: 620, duration: 0.04, type: "triangle", volume: 0.45 },
  scan_success: { freq: 740, duration: 0.12, type: "sine", volume: 0.52 },
  scan_fail: { freq: 180, duration: 0.14, type: "sawtooth", volume: 0.4 },
  quest_complete: { freq: 880, duration: 0.2, type: "triangle", volume: 0.55 },
  token_award: { freq: 980, duration: 0.09, type: "sine", volume: 0.5 },
  item_granted: { freq: 520, duration: 0.14, type: "triangle", volume: 0.48 },
  badge_connect: { freq: 660, duration: 0.14, type: "sine", volume: 0.5 },
  notification: { freq: 440, duration: 0.08, type: "triangle", volume: 0.42 },
  achievement: { freq: 1120, duration: 0.24, type: "square", volume: 0.56 },
  error: { freq: 140, duration: 0.18, type: "sawtooth", volume: 0.38 },
};

const howlCache = new Map<AudioCueKey, Howl>();

function clampSample(value: number): number {
  return Math.max(-1, Math.min(1, value));
}

function waveformSample(type: AudioCueDefinition["type"], phase: number): number {
  switch (type) {
    case "square":
      return phase < 0.5 ? 1 : -1;
    case "triangle":
      return 2 * Math.abs(2 * phase - 1) - 1;
    case "sawtooth":
      return 2 * phase - 1;
    default:
      return Math.sin(2 * Math.PI * phase);
  }
}

function encodePCM16(samples: number[]): string {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  let offset = 0;

  const writeString = (value: string): void => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index));
    }
    offset += value.length;
  };

  const writeUint32 = (value: number): void => {
    view.setUint32(offset, value, true);
    offset += 4;
  };

  const writeUint16 = (value: number): void => {
    view.setUint16(offset, value, true);
    offset += 2;
  };

  writeString("RIFF");
  writeUint32(36 + samples.length * 2);
  writeString("WAVE");
  writeString("fmt ");
  writeUint32(16);
  writeUint16(1);
  writeUint16(1);
  writeUint32(SAMPLE_RATE);
  writeUint32(SAMPLE_RATE * 2);
  writeUint16(2);
  writeUint16(16);
  writeString("data");
  writeUint32(samples.length * 2);

  for (let index = 0; index < samples.length; index += 1) {
    const sample = samples[index] ?? 0;
    view.setInt16(offset, Math.round(clampSample(sample) * 0x7fff), true);
    offset += 2;
  }

  let binary = "";
  const chunkSize = 0x8000;
  for (let start = 0; start < bytes.length; start += chunkSize) {
    const chunk = bytes.subarray(start, start + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return `data:audio/wav;base64,${window.btoa(binary)}`;
}

function buildToneSource(definition: AudioCueDefinition): string {
  const sampleCount = Math.max(1, Math.floor(definition.duration * SAMPLE_RATE));
  const attackSamples = Math.floor(ATTACK_SECONDS * SAMPLE_RATE);
  const releaseSamples = Math.floor(RELEASE_SECONDS * SAMPLE_RATE);
  const samples = new Array<number>(sampleCount);

  for (let index = 0; index < sampleCount; index += 1) {
    const time = index / SAMPLE_RATE;
    const phase = (time * definition.freq) % 1;
    let envelope = 1;
    if (attackSamples > 0 && index < attackSamples) {
      envelope = index / attackSamples;
    }
    if (releaseSamples > 0 && index > sampleCount - releaseSamples) {
      envelope = Math.min(envelope, Math.max(0, (sampleCount - index) / releaseSamples));
    }
    samples[index] = waveformSample(definition.type, phase) * envelope * definition.volume;
  }

  return encodePCM16(samples);
}

function getCueName(name: string): AudioCueKey | null {
  const key = String(name || "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(cues, key) ? (key as AudioCueKey) : null;
}

function getHowl(name: AudioCueKey): Howl {
  const cached = howlCache.get(name);
  if (cached) {
    return cached;
  }
  const definition = cues[name];
  const howl = new Howl({
    src: [buildToneSource(definition)],
    preload: true,
    html5: false,
    volume: 1,
  });
  howlCache.set(name, howl);
  return howl;
}

function play(name: string): boolean {
  const cueName = getCueName(name);
  if (!cueName) {
    return false;
  }
  getHowl(cueName).play();
  return true;
}

function has(name: string): boolean {
  return getCueName(name) !== null;
}

function boot(): void {
  if (audioWindow.__WMU_AUDIO_EVENT_HANDLER__) {
    document.removeEventListener("wmu:audio", audioWindow.__WMU_AUDIO_EVENT_HANDLER__);
  }
  const handler: EventListener = (event) => {
    if (event instanceof CustomEvent) {
      const detail = event.detail;
      if (typeof detail === "string") {
        play(detail);
        return;
      }
      if (detail && typeof detail === "object" && "name" in detail) {
        play(String((detail as { name?: string }).name || ""));
      }
    }
  };
  audioWindow.__WMU_AUDIO_EVENT_HANDLER__ = handler;
  document.addEventListener("wmu:audio", handler);
}

Howler.autoUnlock = true;

const audioWindow = window as WMUAudioWindow;

if (!audioWindow.__WMU_AUDIO_BOOTED) {
  audioWindow.__WMU_AUDIO_BOOTED = true;
  audioWindow.WMUAudio = { play, has };
  boot();
}
